import { FC, Fragment, ReactNode, useCallback } from 'react';

import { BannerModerationStatus } from 'utils/constants/banner';
import { CampaignStatus } from 'utils/constants/dashboard';

import dashboardBannerSwitcherButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_banner_switcher_button_click';

import Tip, { TipPlacement } from 'bloko/blocks/drop/Tip';
import HoverTip from 'bloko/blocks/drop/Tip/HoverTip';
import Link, { LinkAppearance } from 'bloko/blocks/link';

import useTrls from 'hooks/translation/useTrls';

import Switch from 'components/Switch';

import IconStatus from 'components/Banner/Actions/IconStatus';
import Message from 'components/Banner/Actions/Message';
import { Social } from 'components/Banner/Actions/Social/Social';
import { updateBannerStatus } from 'components/Banner/Actions/updateBannerStatus';

import { Banner, FrontendStatusType } from 'store/models/dashboard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Banner/Actions/Actions.module.less';

interface ActionsProps {
    campaignStatus: FrontendStatusType;
    enabled: boolean;
    moderationStatus: BannerModerationStatus;
    campaignId: number;
    bannerId: number;
    states: Banner['states'];
    bannersCount: number;
    isBannerLastEnabled: boolean;
    bannedAsAdvertisement: boolean;
    bannedForAllSources: boolean;
}

const Actions: FC<ActionsProps> = ({
    campaignStatus,
    campaignId,
    bannerId,
    states,
    enabled,
    moderationStatus,
    bannersCount,
    isBannerLastEnabled,
    bannedAsAdvertisement,
    bannedForAllSources,
}) => {
    const dispatch = useDispatch();
    const isBannerSaving = useSelector((state) => state.dashboard.isBannerSaving);

    const trl = useTrls();

    const showSwitch = bannersCount > 1 && campaignStatus === CampaignStatus.Active;
    const showIcon = !showSwitch;

    const onChange = useCallback(() => {
        dashboardBannerSwitcherButtonClick({ activeStatus: String(enabled), bannerId });
        void dispatch(updateBannerStatus(campaignId, bannerId, enabled));
    }, [campaignId, bannerId, enabled, dispatch]);

    const switchNode: ReactNode = isBannerLastEnabled ? (
        <HoverTip
            placement={TipPlacement.Top}
            render={() => trl('banner.actions.switch.tooltip')}
            host={document.body}
            DropElement={Tip}
        >
            <Link appearance={LinkAppearance.Pseudo}>
                <Switch
                    value={enabled}
                    disabled={isBannerSaving || isBannerLastEnabled}
                    onChange={onChange}
                    qa="ad-block__ad-toggler"
                />
            </Link>
        </HoverTip>
    ) : (
        <Switch value={enabled} disabled={isBannerSaving} onChange={onChange} qa="ad-block__ad-toggler" />
    );

    return (
        <Fragment>
            {showSwitch && switchNode}
            {showIcon && <IconStatus campaignStatus={campaignStatus} moderationStatus={moderationStatus} />}
            <div className={styles.bannerStatusMessage}>
                <Message
                    campaignStatus={campaignStatus}
                    campaignId={campaignId}
                    moderationStatus={moderationStatus}
                    enabled={enabled}
                    bannedAsAdvertisement={bannedAsAdvertisement}
                    bannerId={bannerId}
                    bannedForAllSources={bannedForAllSources}
                    moderationReason={states[0].moderationReason}
                    bannersCount={bannersCount}
                />
            </div>
            <div className={styles.bannerStatusSources}>
                {states.map((socialState, key) => (
                    <Social
                        key={key}
                        {...socialState}
                        bannedAsAdvertisement={bannedAsAdvertisement}
                        bannedForAllSources={bannedForAllSources}
                    />
                ))}
            </div>
        </Fragment>
    );
};

export default Actions;
